/**
 * Random String Generator Tool
 *
 * Generate cryptographically random strings using crypto.getRandomValues().
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { IGeneratorTool } from '@/interfaces/ITool';

const CHARSETS: Record<string, string> = {
  alpha: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
  numeric: '0123456789',
  alphanumeric: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
  hex: '0123456789abcdef',
  base64: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/',
  symbols: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?',
};

function generateRandomString(length: number, charset: string): string {
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, v => charset[v % charset.length]).join('');
}

export const RandomStringGeneratorTool: IGeneratorTool = {
  id: 'random-string-generator',
  name: 'Random String Generator',
  description: 'Generate cryptographically random strings',
  version: '1.0.0',
  type: 'generator',
  category: 'Generators',
  keywords: ['random', 'string', 'generate', 'password', 'token', 'key'],

  seo: {
    slug: 'random-string-generator',
    pageTitle: 'Random String Generator - Free Online Tool',
    metaDescription: 'Generate cryptographically random strings with custom length and character sets. Supports alpha, numeric, hex, and custom charsets. Runs in your browser.',
    keywords: ['random string generator', 'random text generator', 'random token generator', 'random key generator'],
    relatedTools: ['password-generator', 'uuid-generator', 'nanoid-generator'],
    schemaType: 'WebApplication',
  },

  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'length',
      type: 'number',
      label: 'Length',
      defaultValue: 32,
      validation: { min: 1, max: 1024 },
      description: 'Length of each string',
    },
    {
      name: 'charset',
      type: 'select',
      label: 'Character Set',
      defaultValue: 'alphanumeric',
      options: ['alpha', 'numeric', 'alphanumeric', 'hex', 'base64', 'symbols', 'custom'],
      description: 'Character set to use',
    },
    {
      name: 'customCharset',
      type: 'string',
      label: 'Custom Characters',
      defaultValue: '',
      description: 'Custom characters (used when charset is "custom")',
    },
    {
      name: 'count',
      type: 'number',
      label: 'Count',
      defaultValue: 1,
      validation: { min: 1, max: 100 },
      description: 'Number of strings to generate',
    },
  ],

  async generate(config: Record<string, unknown>): Promise<string> {
    const length = Math.max(1, Math.min(1024, Number(config.length ?? 32)));
    const charsetName = String(config.charset ?? 'alphanumeric');
    const customCharset = String(config.customCharset ?? '');
    const count = Math.max(1, Math.min(100, Number(config.count ?? 1)));

    const charset = charsetName === 'custom' && customCharset.length > 0
      ? customCharset
      : CHARSETS[charsetName] ?? CHARSETS.alphanumeric;

    const results: string[] = [];
    for (let i = 0; i < count; i++) {
      results.push(generateRandomString(length, charset));
    }

    return results.join('\n');
  },
};
